import { useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import {
  Phone,
  MessageCircle,
  Star,
  MapPin,
  Clock,
  ArrowUpRight,
  Home,
  Building2,
  Briefcase,
  SprayCan,
  Truck,
  ShieldCheck,
  Menu,
  X,
} from "lucide-react";
import useSmoothScroll from "@/hooks/useSmoothScroll";
import { MaskLine, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WA_LINK = "https://wa.me/971569283983";
const TEL_LINK = "tel:+971569283983";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwY2xlYW4lMjBicmlnaHR8ZW58MHx8fHwxNzg1NjM3Nzk3fDA&ixlib=rb-4.1.0&q=85",
  building:
    "https://images.unsplash.com/photo-1694544518330-970f23ed96ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGdsYXNzJTIwZmFjYWRlJTIwZHViYWl8ZW58MHx8fHwxNzg1NjM3Nzk4fDA&ixlib=rb-4.1.0&q=85",
  deep: "https://images.unsplash.com/photo-1512479373983-bd28a541041b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmluZyUyMHdpbmRvd3xlbnwwfHx8fDE3ODU2Mzc3OTh8MA&ixlib=rb-4.1.0&q=85",
  home: "https://images.unsplash.com/photo-1502005097973-6a7082348e28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwY2xlYW4lMjBraXRjaGVufGVufDB8fHx8MTc4NTYzNzc5OHww&ixlib=rb-4.1.0&q=85",
};

const SERVICES = [
  { icon: SprayCan, title: "Deep Cleaning", desc: "Intensive top-to-bottom cleaning that resets every corner of your space.", img: IMAGES.deep },
  { icon: Home, title: "Home Cleaning", desc: "Regular residential cleaning that keeps your villa or apartment spotless.", img: IMAGES.home },
  { icon: Briefcase, title: "Office Cleaning", desc: "Professional workplace cleaning tailored to your business schedule.", img: null },
  { icon: Building2, title: "Building & Facade", desc: "Full building and glass facade cleaning for towers across UAE.", img: IMAGES.building },
  { icon: Truck, title: "Move In / Move Out", desc: "Handover-ready cleaning for tenants, owners and property managers.", img: null },
  { icon: ShieldCheck, title: "Sanitization", desc: "Disinfection & sanitization for a healthier, safer environment.", img: null },
];

const MANIFESTO = [
  { n: "01", t: "Everywhere in UAE", d: "Homes, offices, retail and high-rise buildings — Cleanio brings the same meticulous standard to every address across the city." },
  { n: "02", t: "Detail is the product", d: "We treat cleaning as craft. Trained teams, professional-grade equipment and a checklist that leaves nothing to chance." },
  { n: "03", t: "Simple to book", d: "One message on WhatsApp and a vetted crew is on the way. No call centres, no waiting, no surprises." },
];

const TESTIMONIALS = [
  { name: "Aisha R.", role: "Villa, UAE", text: "Booked a deep clean and the team was punctual, thorough and genuinely professional. The apartment looked brand new." },
  { name: "Marcus L.", role: "Office Manager", text: "We switched our office cleaning to Cleanio and never looked back. Consistent quality every single visit." },
  { name: "Priya S.", role: "Move-out Clean", text: "Got my full deposit back thanks to their move-out clean. Fast, friendly and worth every dirham." },
];

/* ---------------- Logo ---------------- */
const Logo = ({ dark = false, showTagline = false }) => (
  <span className="inline-flex items-center gap-1 select-none">
    <svg viewBox="0 0 120 120" className="w-9 h-9 sm:w-10 sm:h-10 shrink-0" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M30 104 C20 66 42 26 100 18 C96 64 70 100 40 106 C41 84 50 60 84 34 C56 58 42 82 30 104 Z"
        fill="#3EA24A"
      />
    </svg>
    <span className="inline-flex flex-col leading-none">
      <span className={`font-bold tracking-tight text-2xl sm:text-[1.7rem] ${dark ? "text-white" : "text-[#3EA24A]"}`}>
        Cleanio
      </span>
      {showTagline && (
        <span className={`font-semibold uppercase tracking-[0.18em] text-[0.5rem] sm:text-[0.55rem] mt-1.5 ${dark ? "text-neutral-400" : "text-[#243842]"}`}>
          Fresh Spaces · Every Time
        </span>
      )}
    </span>
  </span>
);

/* ---------------- Nav ---------------- */
const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Reviews", href: "#reviews" },
    { label: "Contact", href: "#contact" },
  ];
  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-200"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" aria-label="Cleanio home">
          <Logo />
        </a>
        <nav className="hidden md:flex items-center gap-9 text-sm font-medium">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className="hover:text-[#3EA24A] transition-colors">
              {l.label}
            </a>
          ))}
        </nav>
        <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="nav-whatsapp-btn" className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#3EA24A] transition-colors">
          <MessageCircle className="w-4 h-4" /> Book Now
        </a>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white px-5 py-4 flex flex-col gap-4">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} data-testid={`nav-mobile-${l.label.toLowerCase()}`} className="text-base font-medium">
              {l.label}
            </a>
          ))}
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3EA24A] text-white px-5 py-3 text-sm font-medium">
            <MessageCircle className="w-4 h-4" /> Book on WhatsApp
          </a>
        </div>
      )}
    </motion.header>
  );
};

/* ---------------- Hero ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "12%"]);

  return (
    <section id="top" ref={ref} className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500 mb-7">
            <span className="w-2 h-2 rounded-full bg-[#3EA24A] animate-pulse" /> Cleaning Specialists · UAE
          </motion.div>
          <h1 className="font-display font-semibold tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            <MaskLine delay={0.15}>Spotless</MaskLine>
            <MaskLine delay={0.3} className="text-[#3EA24A]">Spaces,</MaskLine>
            <MaskLine delay={0.45}>Every Time.</MaskLine>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }} className="mt-7 text-lg sm:text-xl text-neutral-600 max-w-md leading-relaxed">
            All cleaning & deep cleaning for homes, offices and buildings — anywhere across UAE.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }} className="mt-9 flex flex-wrap items-center gap-4">
            <a href={TEL_LINK} data-testid="hero-call-btn" className="group inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-7 py-4 text-sm font-semibold hover:bg-[#3EA24A] transition-colors active:scale-[0.98]">
              <Phone className="w-4 h-4" /> Call Now
            </a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="group inline-flex items-center gap-2 rounded-full border-2 border-[#0a0a0a] px-7 py-4 text-sm font-semibold hover:bg-[#DBF2E0] hover:border-[#3EA24A] transition-colors active:scale-[0.98]">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-9 flex items-center gap-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#3EA24A] text-[#3EA24A]" />
              ))}
            </div>
            <span className="text-sm text-neutral-500"><b className="text-[#0a0a0a]">4.9</b> · Trusted by 500+ clients</span>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative">
          <div className="relative overflow-hidden rounded-[2rem] aspect-[4/5] border border-neutral-200">
            <motion.img style={{ y: imgY }} src={IMAGES.hero} alt="Bright, pristine modern living room cleaned by Cleanio in UAE" className="w-full h-[115%] object-cover" loading="eager" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 }} className="absolute -bottom-5 -left-3 sm:left-6 bg-white border border-neutral-200 rounded-2xl px-5 py-4 shadow-sm">
            <p className="text-2xl font-display font-bold text-[#3EA24A]">100%</p>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">Satisfaction focused</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- Marquee ---------------- */
const Marquee = () => {
  const items = ["Deep Cleaning", "Homes", "Offices", "Buildings", "Facades", "Move In / Out", "Sanitization"];
  const row = [...items, ...items];
  return (
    <section className="py-9 sm:py-11 border-y border-neutral-200 overflow-hidden bg-[#0a0a0a]">
      <div className="flex w-max animate-marquee items-center">
        {row.map((it, i) => (
          <div key={i} className="flex items-center">
            <span
              className={`text-4xl sm:text-6xl font-bold tracking-tight mx-8 ${
                i % 2 === 0 ? "text-white" : "text-[#3EA24A]"
              }`}
            >
              {it}
            </span>
            <span className="w-2 h-2 rotate-45 bg-[#3EA24A]" />
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------------- About ---------------- */
const About = () => (
  <section id="about" className="py-24 sm:py-32 max-w-[1400px] mx-auto px-5 sm:px-8">
    <Reveal>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">About Cleanio</p>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl">
        UAE&rsquo;s cleaning specialists, obsessed with the details others miss.
      </h2>
    </Reveal>
    <div className="mt-16 space-y-px">
      {MANIFESTO.map((m, i) => (
        <Reveal key={m.n} delay={i * 0.1}>
          <div className="grid md:grid-cols-[200px_1fr] gap-4 md:gap-12 py-10 border-t border-neutral-200 group">
            <span className="font-display text-6xl sm:text-7xl font-bold text-neutral-200 group-hover:text-[#3EA24A] transition-colors">{m.n}</span>
            <div>
              <h3 className="text-2xl sm:text-3xl font-medium mb-3">{m.t}</h3>
              <p className="text-base sm:text-lg text-neutral-600 leading-relaxed max-w-2xl">{m.d}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Services ---------------- */
const ServiceCard = ({ s, i }) => {
  const Icon = s.icon;
  return (
    <Reveal delay={(i % 3) * 0.08} className={s.img ? "sm:row-span-2" : ""}>
      <div data-testid={`service-card-${i}`} className="h-full flex flex-col rounded-3xl border border-neutral-200 bg-[#f9fafb] p-7 hover:-translate-y-1 hover:border-[#3EA24A] transition-[transform,border-color] duration-300">
        <div className="flex items-center justify-between">
          <div className="w-12 h-12 rounded-2xl bg-white border border-neutral-200 flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#3EA24A]" strokeWidth={2} />
          </div>
          <ArrowUpRight className="w-5 h-5 text-neutral-300" />
        </div>
        <h3 className="mt-6 text-2xl font-medium">{s.title}</h3>
        <p className="mt-2 text-neutral-600 leading-relaxed flex-grow">{s.desc}</p>
        {s.img && (
          <div className="mt-6 overflow-hidden rounded-2xl aspect-[16/10]">
            <img src={s.img} alt={`${s.title} in UAE by Cleanio`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        )}
      </div>
    </Reveal>
  );
};

const Services = () => (
  <section id="services" className="py-24 sm:py-32 bg-[#f9fafb] border-y border-neutral-200">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">What we do</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">Our Services</h2>
        </div>
        <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="services-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0a0a0a] px-6 py-3 text-sm font-semibold hover:bg-[#0a0a0a] hover:text-white transition-colors self-start">
          Request a quote <ArrowUpRight className="w-4 h-4" />
        </a>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.title} s={s} i={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials ---------------- */
const Testimonials = () => (
  <section id="reviews" className="py-24 sm:py-32 max-w-[1400px] mx-auto px-5 sm:px-8">
    <Reveal className="text-center max-w-2xl mx-auto mb-16">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-400 mb-4">Trusted by customers</p>
      <div className="flex items-center justify-center gap-4 mb-5">
        <span className="font-display text-6xl sm:text-7xl font-bold text-[#3EA24A]">4.9</span>
        <div className="text-left">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-[#3EA24A] text-[#3EA24A]" />
            ))}
          </div>
          <p className="text-sm text-neutral-500 mt-1">Rated on Google · 500+ reviews</p>
        </div>
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Loved by homes & businesses across UAE</h2>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-5">
      {TESTIMONIALS.map((t, i) => (
        <Reveal key={t.name} delay={i * 0.1}>
          <figure data-testid={`testimonial-${i}`} className="h-full flex flex-col rounded-3xl border border-neutral-200 bg-white p-8">
            <div className="flex mb-5">
              {[...Array(5)].map((_, j) => (
                <Star key={j} className="w-4 h-4 fill-[#3EA24A] text-[#3EA24A]" />
              ))}
            </div>
            <blockquote className="text-lg leading-relaxed flex-grow">&ldquo;{t.text}&rdquo;</blockquote>
            <figcaption className="mt-6 pt-6 border-t border-neutral-200">
              <p className="font-semibold">{t.name}</p>
              <p className="text-sm text-neutral-500">{t.role}</p>
            </figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- Contact ---------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/leads`, payload);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-neutral-300 rounded-xl px-4 py-3 text-base focus:border-[#3EA24A] focus:outline-none transition-colors";

  return (
    <section id="contact" className="py-24 sm:py-32 bg-[#0a0a0a] text-white">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500 mb-4">Get in touch</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
            Book your <span className="text-[#4FB65C]">clean</span> today.
          </h2>
          <div className="mt-12 space-y-8">
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-[#4FB65C] shrink-0 mt-1" />
              <div>
                <p className="text-sm uppercase tracking-wider text-neutral-500">Service Area</p>
                <p className="text-xl font-medium">Business Bay & across the UAE</p>
              </div>
            </div>
            <a href={TEL_LINK} data-testid="contact-phone-link" className="flex items-start gap-4 group">
              <Phone className="w-6 h-6 text-[#4FB65C] shrink-0 mt-1" />
              <div>
                <p className="text-sm uppercase tracking-wider text-neutral-500">Call / WhatsApp</p>
                <p className="text-xl font-medium group-hover:text-[#4FB65C] transition-colors">+971 56 928 3983</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <Clock className="w-6 h-6 text-[#4FB65C] shrink-0 mt-1" />
              <div>
                <p className="text-sm uppercase tracking-wider text-neutral-500">Opening Hours</p>
                <p className="text-xl font-medium">Mon – Sun · 8:00 AM – 10:00 PM</p>
              </div>
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn" className="inline-flex items-center gap-2 rounded-full bg-[#3EA24A] text-white px-7 py-4 text-sm font-semibold hover:bg-[#2E8339] transition-colors active:scale-[0.98]">
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>
            <a href={TEL_LINK} data-testid="contact-call-btn" className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-7 py-4 text-sm font-semibold hover:border-white transition-colors active:scale-[0.98]">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </div>
        </div>

        <div className="bg-white text-[#0a0a0a] rounded-3xl p-7 sm:p-9">
          <h3 className="text-2xl font-medium mb-6">Request a free quote</h3>
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="input-name" name="name" value={form.name} onChange={handle} placeholder="Full name *" className={inputCls} />
              <input data-testid="input-phone" name="phone" value={form.phone} onChange={handle} placeholder="Phone number *" className={inputCls} />
            </div>
            <input data-testid="input-email" name="email" type="email" value={form.email} onChange={handle} placeholder="Email (optional)" className={inputCls} />
            <select data-testid="input-service" name="service" value={form.service} onChange={handle} className={inputCls + " text-neutral-700"}>
              <option value="">Select a service</option>
              {SERVICES.map((s) => (
                <option key={s.title} value={s.title}>{s.title}</option>
              ))}
            </select>
            <textarea data-testid="input-message" name="message" value={form.message} onChange={handle} placeholder="Tell us about your space…" rows={4} className={inputCls + " resize-none"} />
            <button type="submit" disabled={loading} data-testid="submit-lead-btn" className="w-full rounded-full bg-[#0a0a0a] text-white py-4 font-semibold hover:bg-[#3EA24A] transition-colors active:scale-[0.99] disabled:opacity-60">
              {loading ? "Sending…" : "Send request"}
            </button>
            <p className="text-xs text-neutral-400 text-center">We reply within business hours. No spam, ever.</p>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Footer ---------------- */
const Footer = () => (
  <footer className="bg-[#0a0a0a] text-neutral-400 border-t border-white/10">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
      <Logo dark showTagline />
      <p className="text-sm">© {new Date().getFullYear()} Cleanio · Cleaning Services · UAE</p>
    </div>
  </footer>
);

/* ---------------- Floating WhatsApp ---------------- */
const FloatingWA = () => (
  <motion.a
    href={WA_LINK}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="floating-whatsapp-btn"
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 20 }}
    className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#3EA24A] text-white flex items-center justify-center shadow-lg hover:bg-[#2E8339] transition-colors"
    aria-label="Chat on WhatsApp"
  >
    <MessageCircle className="w-6 h-6" />
  </motion.a>
);

export default function Landing() {
  useSmoothScroll();
  return (
    <main className="bg-white text-[#0a0a0a]">
      <Nav />
      <Hero />
      <Marquee />
      <About />
      <Services />
      <Testimonials />
      <Contact />
      <Footer />
      <FloatingWA />
    </main>
  );
}
